import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, PhoneInputGroup, Avatar } from '@/backoffice/components/ui';
import { LocalizationInputGroup, type LocalizationValue } from '@/backoffice/components/forms/LocalizationInputGroup';
import { useBackofficeRole } from '@/backoffice/hooks/useBackofficeRole';
import api from '@/utils/axios';
import { unwrapItem } from '@/utils/apiHelpers';
import { updateUser } from '@/domains/api/users.api';
import type { ApiUser } from '@/domains/api/types';
import { formatPhoneNumber, splitPhoneNumber } from '@/utils/phone';
import { Loader2, Save, User } from 'lucide-react';

export function SharedProfilePage() {
  const userType = useBackofficeRole();
  const [profile, setProfile] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [firstnames, setFirstnames] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [localization, setLocalization] = useState<LocalizationValue>({
    countryId: null, regionId: null, prefectureId: null, communeId: null, districtId: null, secteurId: null,
  });
  const [phoneCountryCode, setPhoneCountryCode] = useState('+224');
  const [phone, setPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(true);

  useEffect(() => {
    api.get('/me')
      .then((res) => {
        const user = unwrapItem<ApiUser>(res.data.user ?? res.data);
        setProfile(user);
        setFirstnames(user.firstnames);
        setLastname(user.lastname);
        setEmail(user.email ?? '');
        if (user.secteur?.id) {
          setLocalization((prev) => ({ ...prev, secteurId: user.secteur!.id }));
        }
        const split = splitPhoneNumber(user.phone ?? '');
        setPhoneCountryCode(split.countryCode);
        setPhone(split.number);
      })
      .catch(() => setError('Impossible de charger votre profil.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!profile || !firstnames.trim() || !lastname.trim() || !phoneValid) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateUser(profile.id, {
        firstnames: firstnames.trim(),
        lastname: lastname.trim(),
        email: email.trim() || null,
        phone: formatPhoneNumber(phone, phoneCountryCode),
        status: profile.status,
        zone_id: profile.zone?.id ?? 1,
        secteur_id: localization.secteurId ?? undefined,
        type_ids: profile.types?.map((t) => t.id),
        permission_ids: profile.permissions?.map((p) => p.id),
      } as Parameters<typeof updateUser>[1]);
      setProfile(updated);
      setSuccess('Profil mis à jour avec succès.');
    } catch {
      setError('Échec de la mise à jour du profil.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType={userType} title="Mon profil">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType={userType} title="Mon profil">
      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 text-emerald-700 rounded-lg text-sm">{success}</div>}

      <Card className="max-w-3xl">
        <div className="flex items-center gap-4 mb-6">
          <Avatar name={`${firstnames} ${lastname}`} size="lg" />
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2"><User className="w-5 h-5" /> Informations personnelles</h2>
            <p className="text-sm text-gray-500">Mettez à jour vos coordonnées et votre localisation</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Input label="Prénoms" value={firstnames} onChange={(e) => setFirstnames(e.target.value)} />
          <Input label="Nom" value={lastname} onChange={(e) => setLastname(e.target.value)} />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <PhoneInputGroup
            label="Téléphone"
            value={phone}
            countryCode={phoneCountryCode}
            onChange={(value, isValid) => {
              setPhone(value);
              setPhoneValid(isValid);
            }}
            onCountryCodeChange={setPhoneCountryCode}
            required
          />
        </div>

        <LocalizationInputGroup value={localization} onChange={setLocalization} showCoordinates />

        <div className="flex justify-end mt-6">
          <Button leftIcon={<Save className="w-4 h-4" />} isLoading={saving} onClick={handleSave}>
            Enregistrer
          </Button>
        </div>
      </Card>
    </DashboardLayout>
  );
}
